import { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import produce from 'immer'
import {
  Modal as AntModal,
  Button,
  Form as AntForm,
  Col,
  notification as notif,
  Typography,
  Row,
  Switch
} from 'antd'
import { useForm, FormProvider } from 'react-hook-form'
import { useNavigate, generatePath } from 'react-router-dom'
import * as ROUTES from 'admin/routes'
import { useApi } from 'admin/lib/Api'
import { Section, SectionProvider, FormItem } from 'admin/lib/Forms'
import { BodyContentColumn } from 'admin/lib/Layouts'

export function EditNotification({
  notification,
  isNew,
  accountId,
  projectId,
  notificationId
}) {
  const navigate = useNavigate()
  const [submitting, setSubmitting] = useState(false)

  //
  // Form setup & values

  const getFormValues = notification => {
    return notification.map(not => {
      return { ...not, isEnabled: !not.isDisabled }
    })
  }

  const draftItem = getFormValues(notification)

  const form = useForm({
    defaultValues: draftItem,
    mode: 'all',
    criteriaMode: 'firstError'
  })

  //
  // Callbacks

  const api = useApi()
  const onSubmit = useCallback(
    async data => {
      setSubmitting(true)
      const submissionData = produce(draftItem, draft => {
        for (let i = 0; i < draftItem.length; i++) {
          draft[i].label = draftItem[i].label
          draft[i].isDisabled = !data.draftItem[i].isEnabled

          if (draft[i].label == 'Waste') {
            draft[i].template = 'MissingWasteFieldsShort.html'
          } else if (draft[i].label == 'Delivery') {
            draft[i].template = 'ShortDeliveryTemplate.html'
          } else {
            draft[i].template = '404.html'
          }

          draft[i].notificationKey = draftItem[i].notificationKey
        }
      })

      const [, status, error] = await api.updateNotification(
        accountId,
        projectId,
        notificationId,
        submissionData
      )

      if (error) {
        AntModal.error({
          title: 'Error Saving Notification',
          content: (
            <>
              <p>Error, you may wish to try again. </p>
              <p>{`Details: ${status}: ${error}`}</p>
            </>
          )
        })
      } else {
        setSubmitting(false)
        navigate(
          generatePath(ROUTES.NOTIFICATIONS.LIST, {
            accountId: accountId,
            projectId: projectId
          })
        )
        notif.success({
          message: 'Successfully saved notification'
        })
      }
    },
    [accountId, api, draftItem, navigate, notificationId, projectId]
  )

  const handleSubmit = form.handleSubmit(onSubmit)

  return (
    <FormProvider {...form}>
      <SectionProvider>
        <BodyContentColumn>
          <AntForm onFinish={handleSubmit} layout="vertical">
            <Section>
              {({ firstInputRef }) => (
                <>
                  <Typography.Title level={3}>
                    {isNew ? 'Create New Notification' : 'Edit Notification'}
                  </Typography.Title>
                  {draftItem.map((val, index) => {
                    const field = name => `draftItem[${index}].${name}`
                    return (
                      <div key={index}>
                        <Row justify="space-between">
                          <Typography.Title
                            level={5}
                            style={{ paddingLeft: '1em' }}
                          >
                            {val.label}
                          </Typography.Title>
                          <FormItem
                            name={field('isEnabled')}
                            style={{ paddingRight: '1em' }}
                            defaultValue={draftItem[index].isEnabled}
                          >
                            <Switch
                              defaultChecked={draftItem[index].isEnabled}
                            />
                          </FormItem>
                        </Row>
                      </div>
                    )
                  })}
                </>
              )}
            </Section>
            <Col align="middle">
              <Button type="primary" htmlType="submit" disabled={submitting}>
                {isNew ? 'Create' : 'Update'}
              </Button>
            </Col>
          </AntForm>
        </BodyContentColumn>
      </SectionProvider>
    </FormProvider>
  )
}

EditNotification.propTypes = {
  notification: PropTypes.array,
  isNew: PropTypes.bool.isRequired,
  accountId: PropTypes.string.isRequired,
  notificationId: PropTypes.string
}
